import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { getAdminRoutes } from "./admin-routes";
import { AUTH_ROUTES } from "./auth-routes";
import { CLIENT_REALTIME_FEATURES } from "./client-realtime";
import { getClientRoutes } from "./client-routes";
import PrivateRoute from "./PrivateRoute";
import { renderRoutes } from "./route-renderer";
import TenantGate from "./TenantGate";

import Layout from "@/components/layout/Layout";
import { ThemeProvider } from "@/components/theme-provider";
import { DrawerRoot } from "@/components/ui/v2/drawer-root";
import { Toaster } from "@/components/ui/v2/sonner";
import { TenantClientSelectorProvider } from "@/components/ui/v2/tenant-client-selector";
import UnavailablePage from "@/components/ui/v2/unavailable-page";
import { ROUTES } from "@/constants/routes";
import AuthLayout from "@/features/auth/components/AuthLayout";
import { InsightsProvider } from "@/features/insights/context";
import { NotificationProvider } from "@/features/notifications/context";
import { ClientRealtimeSubscriber } from "@/features/realtime";
import { AuthProvider } from "@/lib/contexts/Auth/AuthProvider";
import { PopupProvider } from "@/lib/contexts/Popup/PopupProvider";
import { TenantProvider } from "@/lib/contexts/Tenant/TenantProvider";
import { useAuth } from "@/lib/hooks/useAuth";

const Content = () => {
  const { tenantId, context, accessToken, clientId } = useAuth();
  const navigate = useNavigate();
  const pathname = useLocation().pathname;

  useEffect(() => {
    if (
      !accessToken &&
      !AUTH_ROUTES.map((route) => route.path).includes(pathname)
    ) {
      navigate(ROUTES.login, { replace: true });
    }
  }, [pathname, navigate, accessToken]);

  return (
    <Routes>
      {/* Auth Routes */}
      <Route element={<AuthLayout />}>{renderRoutes(AUTH_ROUTES)}</Route>

      {/* Fallback Routes */}
      <Route path="*" element={<UnavailablePage variant="notFound" />} />

      <Route element={<PrivateRoute />}>
        <Route
          element={
            <NotificationProvider>
              {/* Admins get theirs from ClientRoute, for the client in the URL. */}
              {context === "client" && (
                <ClientRealtimeSubscriber clientId={clientId} features={CLIENT_REALTIME_FEATURES} />
              )}
              <TenantProvider>
                <InsightsProvider>
                  <TenantGate />
                </InsightsProvider>
              </TenantProvider>
            </NotificationProvider>
          }
        >
          <Route element={<Layout />}>
            {/* Client Routes - Available to both admins and clients */}
            {context === "client" && renderRoutes(getClientRoutes())}
            {/* Admin-only Routes */}
            {context === "admin" && renderRoutes(getAdminRoutes({ tenantId }))}
            <Route
              path="/"
              element={
                <Navigate
                  to={context === "admin" ? `/${ROUTES.clients}` : `/${ROUTES.home}`}
                  replace
                />
              }
            />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
};

const AppRouter = () => {
  return (
    <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
      <Sentry.ErrorBoundary fallback={({ resetError }) => <UnavailablePage variant="error" resetError={resetError} />}>
        <AuthProvider>
          <TenantClientSelectorProvider>
            <PopupProvider>
              <Content />
              <Toaster />
              <DrawerRoot />
            </PopupProvider>
          </TenantClientSelectorProvider>
        </AuthProvider>
      </Sentry.ErrorBoundary>
    </ThemeProvider>
  );
};

export default AppRouter;
